import { GlobalMessages } from '@shared/localization/messages';
import { type FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type { EstimationUnit, TenantResponse } from '@/api/schema/data-contracts';
import { ApiEstimationUserAssignmentType } from '@/api/schema/data-contracts';
import { Button } from '@/components/atoms/Button';
import { SelectFormField } from '@/components/atoms/SelectFormField';
import { TextFormField } from '@/components/atoms/TextFormField';
import { UserAutocompleteFormField } from '@/components/atoms/UserAutocompleteFormField';
import { ProjectInfoMessages } from '@/components/organism/NewEstimation/ProjectBasics/ProjectInfo/messages';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { estimationUnitMessageDefinitions } from '@/context/localization/enums/messages';
import { useLanguages } from '@/hooks/languages/useLanguages';
import { useMessages } from '@/hooks/messages';
import type { EstimationProjectUsers } from '@/hooks/useGetEstimationProjectUsers';
import { EstimationUnits } from '@/types';
import { sortExpressions } from '@/utils/sorting';

import type { ProjectBasicsModel } from '../../model';

interface ProjectFormProps {
  tenant: TenantResponse;
  estimationData: EstimationProjectUsers;
  onSubmit: (formModel: ProjectBasicsModel) => void;
  isReadOnly: boolean;
  isProcessing: boolean;
}

export const ProjectForm: FC<ProjectFormProps> = ({
  tenant,
  estimationData,
  onSubmit,
  isReadOnly,
  isProcessing,
}) => {
  const { getLanguageDisplayName } = useLanguages();
  const projectMessages = useMessages(ProjectInfoMessages);
  const estimationUnitMessages = useMessages(estimationUnitMessageDefinitions);
  const globalMessages = useMessages(GlobalMessages);

  const formModelInitial = useMemo<ProjectBasicsModel>(() => {
    const { estimation, project, users } = estimationData;
    const projectBasics: ProjectBasicsModel = {
      projectName: project.name,
      projectTypeId: project.projectTypeId,
      methodologyId: estimation.methodologyId,
      defaultEstimationUnit: estimation.unit,
      estimationLanguage: tenant.defaultCultureName,
      planningResponsibleId: estimation.planningResponsibleId,
      projectReviewerId:
        users.find(
          (user) => user.estimationUserAssignmentType === ApiEstimationUserAssignmentType.REVIEWER,
        )?.id ?? '',
      additionalContributorIds:
        users
          .filter(
            (user) =>
              user.estimationUserAssignmentType === ApiEstimationUserAssignmentType.CONTRIBUTOR,
          )
          ?.map((user) => user.id) ?? [],
    };
    return projectBasics;
  }, [estimationData, tenant]);

  const { reset, handleSubmit, control } = useForm<ProjectBasicsModel>({
    defaultValues: formModelInitial,
  });

  useEffect(() => {
    reset(formModelInitial);
  }, [reset, formModelInitial]);

  const projectTypeOptions = useMemo(
    () =>
      estimationData?.projectTypes.sort(sortExpressions.byOrder).map((x) => ({
        label: x.name,
        value: x.id,
      })) ?? [],
    [estimationData],
  );

  const methodologyOptions = useMemo(
    () =>
      estimationData?.methodologies.sort(sortExpressions.byOrder).map((x) => ({
        label: x.name,
        value: x.id,
      })) ?? [],
    [estimationData],
  );

  const estimationUnitOptions = useMemo(
    () =>
      Object.keys(EstimationUnits).map((x) => ({
        label: estimationUnitMessages[x as EstimationUnit](),
        value: x,
      })) ?? [],
    [estimationUnitMessages],
  );

  const tenantLanguagesOptions = useMemo(
    () =>
      tenant?.supportedCultureNames.map((x) => ({
        label: getLanguageDisplayName(x, true) ?? '',
        value: x,
      })) ?? [],
    [getLanguageDisplayName, tenant],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {estimationData && (
        <div className='space-y-5'>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div>
              <label htmlFor='projectBasics.projectName'>
                {projectMessages.projectNameLabel()}
              </label>
              {isReadOnly ? (
                estimationData.project.name
              ) : (
                <TextFormField
                  control={control}
                  fieldPath='projectName'
                  readOnly={isProcessing}
                  rules={{
                    required: true,
                    maxLength: entityFieldSettings.projectEntity.nameMaxLength,
                  }}
                />
              )}
            </div>
            <div>
              <label htmlFor='projectBasics.defaultEstimationUnit'>
                {projectMessages.defaultEstimationUnitLabel()}
              </label>
              <SelectFormField
                control={control}
                fieldPath='defaultEstimationUnit'
                options={estimationUnitOptions}
                isDisabled
                rules={{ required: true }}
                classNames={{
                  control: () => 'dark:!border-c1',
                }}
              />
            </div>
            <div>
              <label htmlFor='projectBasics.estimationLanguage'>
                {projectMessages.estimationLanguageLabel()}
              </label>
              <SelectFormField
                control={control}
                fieldPath='estimationLanguage'
                options={tenantLanguagesOptions}
                isDisabled
                rules={{ required: true }}
                classNames={{
                  control: () => 'dark:!border-c1 ',
                }}
              />
            </div>
            <div>
              <label htmlFor='projectBasics.projectTypeId'>
                {projectMessages.projectTypeLabel()}
              </label>
              <SelectFormField
                control={control}
                fieldPath='projectTypeId'
                options={projectTypeOptions}
                isDisabled
                rules={{ required: true }}
                classNames={{
                  control: () => 'dark:!border-c1',
                }}
              />
            </div>
            <div>
              <label htmlFor='projectBasics.methodologyId'>
                {projectMessages.methodologyLabel()}
              </label>
              <SelectFormField
                control={control}
                fieldPath='methodologyId'
                options={methodologyOptions}
                isDisabled
                rules={{ required: true }}
                classNames={{
                  control: () => 'dark:!border-c1',
                }}
              />
            </div>
            <div>
              <label htmlFor='projectBasics.planningResponsibleId'>
                {projectMessages.planningResponsibleLabel()}
              </label>
              <UserAutocompleteFormField
                control={control}
                fieldPath='planningResponsibleId'
                isDisabled={isReadOnly || isProcessing}
                rules={{ required: true }}
              />
            </div>
            <div>
              <label htmlFor='projectBasics.projectReviewerId'>
                {projectMessages.reviewerLabel()}
              </label>
              <UserAutocompleteFormField
                control={control}
                fieldPath='projectReviewerId'
                isDisabled={isReadOnly || isProcessing}
                permissions={['CAN_BE_REVIEWER']}
              />
            </div>
            <div>
              <label htmlFor='projectBasics.additionalContributorId'>
                {projectMessages.additionalContributorsLabel()}
              </label>
              <UserAutocompleteFormField
                control={control}
                fieldPath='additionalContributorIds'
                isDisabled={isReadOnly || isProcessing}
                isMulti
                data-testid='additional-contributors-select'
              />
            </div>
          </div>
          {!isReadOnly && (
            <Button
              data-testid='project-form-save-button'
              type='submit'
              variant='primary'
              disabled={isProcessing}
              isProcessing={isProcessing}
            >
              {globalMessages.save()}
            </Button>
          )}
        </div>
      )}
    </form>
  );
};
